import React from "react";
import PropTypes from "prop-types";

/**
 * single_select：单值下拉框（单选）。
 *
 * 设计目标：与 single_input 共享「单值 component」契约——componentData 为顶层字符串；
 * 不同之处在于 single_select 通过 schema.options 限定可选值，UI 渲染 <select>，
 * 不暴露 value_type / 类型下拉。
 *
 * 行为约定：
 * - value 取自 options[i].value；空值（null / ""）走 UI 顶部"请选择"占位 option
 *   （value=""），用于「必填未填」的视觉提示与「主动清空」入口。
 * - required=true 且 value 为空 → data-empty="true"，由 CSS 给出红框（与 single_input 一致）。
 * - onChange(value)：单参数，无 valueType。
 * - isEdit=false → disabled（只读查看）。
 *
 * 选项格式（schema 端已归一化为 OptionItem）：
 *   - 简写：options: [a, b]            → 视为 [{value:"a",name:"a"},{value:"b",name:"b"}]
 *   - 完整：options: [{value, name, color?}, ...]
 *
 * 调用方应在 PropertyPanel 用 `key={node.id}` 让 SingleSelect 在切换节点时干净 remount，
 * 避免把上一个节点的 selected value 错误带到下一个节点。
 */
function SingleSelect({
  value,
  options,
  placeholder,
  required,
  isEdit,
  onChange,
}) {
  const isRequired = Boolean(required);
  const isEmpty = value === null || value === undefined || value === "";

  // 归一化 options：兼容两种 YAML 形态
  //   - 字符串列表 ["add", "mul"] → [{value: "add", name: "add"}, ...]
  //   - 对象列表 [{value, name, color?}, ...] → 原样
  // Python schema 端 _coerce_options 已经做过同样的归一化；这里兜一手测试场景
  // 直接传 options 字符串数组、或日后 JS 侧自行构造的 schema。
  const rawOpts = Array.isArray(options) ? options : [];
  const opts = rawOpts.map((o) =>
    typeof o === "string" ? { value: o, name: o, color: "" } : o
  );

  // 当前 value 是否命中 options：未命中时浏览器会把 select 显示成空白（首个 option）
  // 这是预期的——意味着 schema 与数据不一致；用户重新选择即可恢复
  const currentValue = isEmpty ? "" : String(value);

  const handleChange = (e) => {
    const v = e.target.value;
    // 空串归一为 null（与 single_input 同源语义：空 = 未选）
    if (v === "") {
      onChange?.(null);
      return;
    }
    onChange?.(v);
  };

  return (
    <div className="single-select">
      <select
        className="tfe-form-control kv-input-control single-select-control"
        disabled={!isEdit}
        data-required={isRequired ? "true" : "false"}
        data-empty={isRequired && isEmpty ? "true" : "false"}
        value={currentValue}
        onChange={handleChange}
      >
        {/* 占位 option：value="" 表示「未选择」，required 时触发红框 */}
        <option value="">{placeholder || "— 请选择 —"}</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

SingleSelect.propTypes = {
  /** 当前值；为 null / "" 表示未选择 */
  value: PropTypes.any,
  /**
   * 选项列表。元素两种形态皆可：
   *   - 字符串（shorthand）："add" → {value: "add", name: "add"}
   *   - 对象：{value, name, color?}
   * 与 schema 端 _coerce_options 行为一致；运行时统一归一化为对象。
   */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        color: PropTypes.string,
      }),
    ])
  ),
  /** 占位文案（同时作为"未选择"option 的显示文本） */
  placeholder: PropTypes.string,
  /** 必填：空值时显示红框 */
  required: PropTypes.bool,
  /** 编辑模式；false 时 select disabled */
  isEdit: PropTypes.bool,
  /** 值变更回调：onChange(value)；空选择时 value=null */
  onChange: PropTypes.func.isRequired,
};

export default SingleSelect;